import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import { HttpShippings } from "../../http";
import { EntityWithIdFields } from "../../types/baseEntities";
import { OrderShippingViewDTO, OrderShippingViewDTOFields } from "../../types/purchaseOrder/Order";
import { Icon } from "../liquid/LiquidPrimitives";

interface ShippingCalculatorProps {
  onCalculateShipping: (zipCode: string) => void;
  onSelectShipping?: (cost: number) => void;
  productId: string;
  productVariantId?: string;
  quantity: number;
}

export default function ShippingCalculator({
  onCalculateShipping,
  onSelectShipping,
  productId,
  productVariantId,
  quantity,
}: ShippingCalculatorProps) {
  const [zipCode, setZipCode] = useState("");
  const [shippingOptions, setShippingOptions] = useState<OrderShippingViewDTO[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!zipCode.trim()) return;

    onCalculateShipping(zipCode);
    setLoading(true);
    setSelectedOptionId("");
    setShippingOptions([]);

    try {
      const options = await HttpShippings.calculateShippingOptions({
        codigoPostal: zipCode,
        productosOrden: [
          {
            idProducto: productId,
            idProductoVariante: productVariantId ?? "",
            cantidad: quantity,
          },
        ],
      });
      setShippingOptions(options);
    } catch (error) {
      console.error("Error calculando envio:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number, fixedCost: boolean, integratedCost: boolean) => {
    if (!fixedCost && !integratedCost) return "A convenir";
    return price === 0
      ? "Gratis"
      : `$${price.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="lg-strong" style={{ padding: "22px 24px", borderRadius: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <Icon.truck />
        <span className="eyebrow">Medios de envio</span>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: "4px 4px 4px 16px",
          borderRadius: 999,
          background: "rgba(10,10,10,0.04)",
          border: "1px solid rgba(10,10,10,0.06)",
        }}
      >
        <input
          placeholder="Tu codigo postal"
          value={zipCode}
          onChange={(event) => setZipCode(event.target.value)}
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
            flex: 1,
            fontSize: 14,
            padding: "10px 0",
            minWidth: 0,
          }}
        />
        <button
          type="submit"
          style={{
            padding: "10px 20px",
            borderRadius: 999,
            border: "none",
            background: "var(--ink-900)",
            color: "var(--cream-50)",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
            minWidth: 96,
          }}
        >
          {loading ? <CircularProgress size={14} sx={{ color: "var(--cream-50)" }} /> : "Calcular"}
        </button>
      </form>

      {shippingOptions.length > 0 && (
        <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
          {shippingOptions.map((option) => {
            const optionId = option[EntityWithIdFields.Id];
            const selected = selectedOptionId === optionId;
            const cost = option[OrderShippingViewDTOFields.Cost];
            const fixed = option[OrderShippingViewDTOFields.FixedCost];
            const integrated = option[OrderShippingViewDTOFields.IntegratedCost];

            return (
              <button
                type="button"
                key={optionId}
                onClick={() => {
                  setSelectedOptionId(optionId);
                  onSelectShipping?.(cost);
                }}
                style={{
                  padding: "14px 16px",
                  borderRadius: 16,
                  background: selected ? "rgba(10,10,10,0.06)" : "rgba(10,10,10,0.03)",
                  border: selected ? "1px solid rgba(10,10,10,0.18)" : "1px solid rgba(10,10,10,0.06)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 14,
                  textAlign: "left",
                }}
              >
                <span>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 500 }}>{option[OrderShippingViewDTOFields.OptionName]}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: "var(--ink-500)", marginTop: 2 }}>
                    {option[OrderShippingViewDTOFields.FormattedDescription]}
                  </span>
                </span>
                <span className="mono" style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {formatPrice(cost, fixed, integrated)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
